// Pickup Availability Component - Flexible Selectors
// Fixed: Singleton pattern, proper cleanup, event delegation

function stripPickupSuffix(name) {
  return (name || '').replace(/\s*\(pickup\)\s*$/i, '').trim();
}

class PickupAvailability {
  constructor() {
    // Prevent multiple instances - this is critical for memory management
    if (window.pickupAvailabilityInstance) {
      // Just refresh the existing instance instead of creating new one
      window.pickupAvailabilityInstance.refresh();
      return window.pickupAvailabilityInstance;
    }

    // Try multiple possible selectors for the store availability container
    this.container =
      document.querySelector('[data-section-type="store-availability"]') ||
      document.querySelector('.store-availability') ||
      document.querySelector('#StoreAvailabilityModal') ||
      document.querySelector('.product__store-availability-container');

    // Get drawer element by ID or class (including global store picker drawer)
    this.drawer =
      document.getElementById('StoreAvailabilityDrawer') ||
      document.getElementById('StorePickerDrawer') ||
      document.querySelector('.store-availabilities-modal');

    // If neither container nor drawer exists, don't proceed
    if (!this.container && !this.drawer) {
      return;
    }

    // Mark as initialized
    window.pickupAvailabilityInstance = this;

    // Flag to prevent MutationObserver feedback loop
    this._isUpdating = false;

    // Initialize component
    this.init();
  }

  init() {
    // Check if user has a preferred store using LocationUtils
    const store = LocationUtils.getStore();
    this.userStoreId = store?.id || null;
    this.userStoreName = store?.name || null;

    // Store bound handlers for cleanup
    this._boundHandlers = {
      storeSelected: this.handleStoreSelected.bind(this),
      locationChanged: this.handleLocationChanged.bind(this),
      drawerOpenerClick: this.handleDrawerOpenerClick.bind(this),
      variantChanged: this.handleVariantChange.bind(this),
      pickupUpdated: this.handlePickupUpdated.bind(this)
    };

    // Add event listeners using delegation
    this.setupEventListeners();

    // MutationObserver removed - was causing flashing loop
    // this.setupMutationObserver();

    // If drawer exists, add store selection buttons
    if (this.drawer) {
      this.addStoreSelectionButtons();
    }

    // If user has a preferred store, prioritize it
    if (this.userStoreId && this.userStoreName) {
      if (this.container) {
        this.updateMainAvailability();
      }
      if (this.drawer) {
        this.reorderDrawerStores();
      }
    }
  }

  // Refresh method for when instance already exists
  refresh() {
    // Re-find DOM elements in case they changed
    this.container =
      document.querySelector('[data-section-type="store-availability"]') ||
      document.querySelector('.store-availability') ||
      document.querySelector('#StoreAvailabilityModal') ||
      document.querySelector('.product__store-availability-container');

    this.drawer =
      document.getElementById('StoreAvailabilityDrawer') ||
      document.getElementById('StorePickerDrawer') ||
      document.querySelector('.store-availabilities-modal');

    // Re-read user preferences using LocationUtils
    const store = LocationUtils.getStore();
    this.userStoreId = store?.id || null;
    this.userStoreName = store?.name || null;

    // Update displays if user has preferred store
    if (this.userStoreId && this.userStoreName) {
      if (this.drawer) {
        this.addStoreSelectionButtons();
      }
      if (this.container) {
        this.updateMainAvailability();
      }
      if (this.drawer) {
        this.reorderDrawerStores();
      }
    }
  }

  // Destroy method for proper cleanup
  destroy() {
    // Disconnect mutation observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Remove event listeners
    if (this._boundHandlers) {
      document.removeEventListener('store:selected', this._boundHandlers.storeSelected);
      document.removeEventListener('location:changed', this._boundHandlers.locationChanged);
      document.removeEventListener('click', this._boundHandlers.drawerOpenerClick);
      document.removeEventListener('variant:changed', this._boundHandlers.variantChanged);
      document.removeEventListener('variantchange', this._boundHandlers.variantChanged);
      document.removeEventListener('product:variant-change', this._boundHandlers.variantChanged);
      document.removeEventListener('pickup-availability:updated', this._boundHandlers.pickupUpdated);
    }

    // Clear singleton reference
    window.pickupAvailabilityInstance = null;
  }

  // Event handlers as methods (for proper binding/unbinding)
  handleStoreSelected(event) {
    if (event.detail && event.detail.id) {
      this.userStoreId = event.detail.id;
      this.userStoreName = event.detail.name;

      this.updateMainAvailability();

      if (this.drawer) {
        this.reorderDrawerStores();
      }

      if (this.drawer && this.isDrawerOpen()) {
        this.updateDrawerStoreButtons();
      }
    }
  }

  handleLocationChanged() {
    if (this.drawer && this.isDrawerOpen()) {
      setTimeout(() => {
        this.addStoreSelectionButtons();
        this.updateDrawerStoreButtons();
      }, 300);
    }
  }

  handleDrawerOpenerClick(event) {
    // Event delegation - check if click was on a drawer opener (including global store picker)
    const opener = event.target.closest('.js-drawer-open-availability, .js-drawer-open-store-picker, [aria-controls="StoreAvailabilityDrawer"], [aria-controls="StorePickerDrawer"], [data-store-availability-modal-opener]');
    if (opener) {
      // Re-find drawer in case it's on a different page
      this.drawer =
        document.getElementById('StoreAvailabilityDrawer') ||
        document.getElementById('StorePickerDrawer') ||
        document.querySelector('.store-availabilities-modal');

      setTimeout(() => {
        this.addStoreSelectionButtons();
        this.updateDrawerStoreButtons();
      }, 300);
    }
  }

  handleVariantChange() {
    setTimeout(() => {
      this.refresh();
    }, 500);
  }

  handlePickupUpdated() {
    if (this.userStoreId) {
      setTimeout(() => {
        this.updateMainAvailability();
        this.reorderDrawerStores();
      }, 100);
    }
  }

  setupEventListeners() {
    // Use stored bound handlers for proper cleanup later
    document.addEventListener('store:selected', this._boundHandlers.storeSelected);
    document.addEventListener('location:changed', this._boundHandlers.locationChanged);

    // Use event delegation instead of adding listeners to each opener
    document.addEventListener('click', this._boundHandlers.drawerOpenerClick);

    // Variant change events
    document.addEventListener('variant:changed', this._boundHandlers.variantChanged);
    document.addEventListener('variantchange', this._boundHandlers.variantChanged);
    document.addEventListener('product:variant-change', this._boundHandlers.variantChanged);

    // Pickup availability updates
    document.addEventListener('pickup-availability:updated', this._boundHandlers.pickupUpdated);
  }

  setupMutationObserver() {
    if (this.container || this.drawer) {
      // Debounce mutation handling to prevent excessive processing
      let mutationTimeout = null;

      this.observer = new MutationObserver((mutations) => {
        // Skip if we're currently making our own DOM updates (prevents feedback loop)
        if (this._isUpdating) {
          return;
        }

        let hasRelevantChanges = false;

        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            const nodes = [...mutation.addedNodes, ...mutation.removedNodes];

            const isRelevant = nodes.some(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Ignore our own additions (buttons, indicators, distance elements)
                if (node.classList?.contains('store-availability-favorite') ||
                    node.classList?.contains('preferred-store-indicator') ||
                    node.classList?.contains('store-distance') ||
                    node.classList?.contains('store-availability__small')) {
                  return false;
                }
                return node.classList?.contains('store-availability') ||
                       node.querySelector?.('.store-availability') ||
                       node.id === 'StoreAvailabilityDrawer' ||
                       node.dataset?.sectionType === 'store-availability';
              }
              return false;
            });

            if (isRelevant) {
              hasRelevantChanges = true;
              break;
            }
          }
        }

        if (hasRelevantChanges && this.userStoreId && this.userStoreName) {
          // Debounce: clear previous timeout and set new one
          if (mutationTimeout) {
            clearTimeout(mutationTimeout);
          }

          mutationTimeout = setTimeout(() => {
            this.refresh();
          }, 200);
        }
      });

      // Observe only if we have a target
      const targetElement = document.querySelector('main') || document.body;
      this.observer.observe(targetElement, {
        childList: true,
        subtree: true
      });
    }
  }

  isDrawerOpen() {
    if (!this.drawer) return false;

    return (
      this.drawer.getAttribute('aria-hidden') === 'false' ||
      this.drawer.classList.contains('is-active') ||
      this.drawer.classList.contains('drawer--is-open') ||
      this.drawer.hasAttribute('open') ||
      window.getComputedStyle(this.drawer).display !== 'none'
    );
  }

  addStoreSelectionButtons() {
    if (!this.drawer) {
      return;
    }

    // Set flag to prevent MutationObserver feedback loop
    this._isUpdating = true;

    const storeItems =
      this.drawer.querySelectorAll('.store-availability') ||
      this.drawer.querySelectorAll('[data-store-availability-container]') ||
      this.drawer.querySelectorAll('.store-availabilities-list__item');

    if (storeItems.length === 0) {
      return;
    }

    storeItems.forEach((item, index) => {
      const storeNameElement =
        item.querySelector('strong') ||
        item.querySelector('.store-availability-name') ||
        item.querySelector('h3');

      if (!storeNameElement) {
        return;
      }

      const storeName = storeNameElement.textContent.trim();

      let storeId = item.dataset.storeId;
      if (!storeId) {
        storeId = `store-${index}-${storeName.replace(/\s+/g, '-').toLowerCase()}`;
        item.setAttribute('data-store-id', storeId);
        item.setAttribute('data-store-name', storeName);
      }

      let storeCoordinates = null;

      if (typeof window.storeLocations !== 'undefined') {
        const matchingStore = window.storeLocations.find(store => {
          const name1 = store.name.toLowerCase().replace(/[^a-z0-9]/g, '');
          const name2 = storeName.toLowerCase().replace(/[^a-z0-9]/g, '');
          return name1 === name2 || name1.includes(name2) || name2.includes(name1);
        });

        if (matchingStore && matchingStore.coordinates) {
          storeCoordinates = matchingStore.coordinates;
          item.dataset.latitude = matchingStore.coordinates.lat;
          item.dataset.longitude = matchingStore.coordinates.lng;
        }
      }

      const addressElements = item.querySelectorAll('address');

      const storeInfo =
        item.querySelector('.store-availability__info') ||
        item.querySelector('.store-availabilities-list__location-info') ||
        item.querySelector('address') ||
        item;

      if (storeCoordinates) {
        const userCoordinates = this.getUserCoordinates();
        if (userCoordinates) {
          const distance = window.calculateDistance(
            userCoordinates.lat, userCoordinates.lng,
            storeCoordinates.lat, storeCoordinates.lng
          );

          let distanceDisplay;
          if (distance < 0.1) {
            distanceDisplay = `${distance.toFixed(2)} miles away`;
          } else if (distance < 1) {
            distanceDisplay = `${distance.toFixed(1)} miles away`;
          } else {
            distanceDisplay = `${Math.round(distance * 10) / 10} miles away`;
          }

          const distanceElement = item.querySelector('.store-distance');
          if (!distanceElement) {
            const distanceDiv = document.createElement('div');
            distanceDiv.className = 'store-distance';
            distanceDiv.style.cssText = 'font-size: 12px; color: #666; margin-top: 4px;';
            distanceDiv.textContent = distanceDisplay;
            storeInfo.appendChild(distanceDiv);
          } else {
            distanceElement.textContent = distanceDisplay;
          }
        }
      }

      if (!item.querySelector('.store-availability-favorite')) {
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'store-availability__small store-availability__small--link';
        buttonContainer.innerHTML = `
          <button class="store-availability-favorite btn btn--small"
                  type="button"
                  aria-label="Set ${storeName} as your location"
                  data-store-id="${storeId}"
                  data-store-name="${storeName}">
            Set as my store
          </button>
        `;

        storeInfo.appendChild(buttonContainer);

        const button = buttonContainer.querySelector('button');
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const storeZip = item.dataset.storeZip || null;
          this.setUserStore(storeId, storeName, storeZip);
        });
      }
    });

    this.updateDrawerStoreButtons();

    // Reset flag after DOM updates are complete
    this._isUpdating = false;
  }

  getUserCoordinates() {
    const savedCoordinates = localStorage.getItem('userSearchCoordinates');
    if (savedCoordinates) {
      try {
        const coords = JSON.parse(savedCoordinates);
        if (coords && coords.latitude && coords.longitude) {
          return {
            lat: coords.latitude,
            lng: coords.longitude
          };
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    if (typeof userLocation !== 'undefined' && userLocation) {
      return userLocation;
    }

    return null;
  }

  setUserStore(storeId, storeName, storeZip = null) {
    // Use LocationUtils for storage and event dispatch
    LocationUtils.setStore(storeId, storeName, storeZip);

    this.userStoreId = storeId;
    this.userStoreName = storeName;

    this.updateMainAvailability();
    this.reorderDrawerStores();
    this.updateDrawerStoreButtons();

    setTimeout(() => {
      const closeButton =
        this.drawer?.querySelector('.js-drawer-close') ||
        this.drawer?.querySelector('[data-modal-close]') ||
        this.drawer?.querySelector('.close-modal');

      if (closeButton) {
        closeButton.click();
      }
    }, 300);
  }

  updateMainAvailability() {
    if (!this.container || !this.userStoreId) return;

    const mainAvailabilityContainer =
      this.container.querySelector('.store-availability') ||
      this.container.querySelector('[data-store-availability-container]');

    if (!mainAvailabilityContainer) return;

    const storeNameStrong = mainAvailabilityContainer.querySelector('.store-availability__info div strong');
    if (storeNameStrong) {
      storeNameStrong.textContent = this.userStoreName;
    }
  }

  reorderDrawerStores() {
    if (!this.drawer) return;

    // Set flag to prevent MutationObserver feedback loop
    this._isUpdating = true;

    const scrollableContainer = this.drawer.querySelector('.drawer__scrollable');
    if (!scrollableContainer) {
      this._isUpdating = false;
      return;
    }

    const storeItems = scrollableContainer.querySelectorAll('.store-availability');
    if (storeItems.length === 0) {
      this._isUpdating = false;
      return;
    }

    // Remove all existing preferred-store-indicator elements first
    scrollableContainer.querySelectorAll('.preferred-store-indicator').forEach(el => el.remove());

    // Get user coordinates for distance calculation
    // First try saved coordinates, then fall back to selected store's coordinates
    let userCoords = null;

    const location = typeof LocationUtils !== 'undefined' ? LocationUtils.getLocation() : null;
    if (location?.coordinates) {
      userCoords = location.coordinates;
    } else if (this.userStoreId && window.storeLocations) {
      // Use selected store's coordinates as fallback
      const selectedStore = window.storeLocations.find(s => s.id === this.userStoreId);
      if (selectedStore?.coordinates) {
        userCoords = selectedStore.coordinates;
      }
    }

    // Build array of stores with distances
    const storesWithDistance = [];
    storeItems.forEach(item => {
      const storeName = stripPickupSuffix(item.querySelector('strong')?.textContent?.trim());
      const storeData = window.storeLocations?.find(s => stripPickupSuffix(s.name) === storeName);

      let distance = Infinity;
      if (userCoords && storeData?.coordinates) {
        distance = LocationUtils.calculateDistance(
          userCoords.lat, userCoords.lng,
          storeData.coordinates.lat, storeData.coordinates.lng
        );
      }

      const isPreferred = this.userStoreName && storeName === stripPickupSuffix(this.userStoreName);
      storesWithDistance.push({ element: item, distance, isPreferred, storeName });
    });

    // Sort: preferred store first, then by distance (nearest to furthest)
    storesWithDistance.sort((a, b) => {
      if (a.isPreferred) return -1;
      if (b.isPreferred) return 1;
      return a.distance - b.distance;
    });

    // Re-append elements in sorted order
    const container = storeItems[0].parentElement;
    storesWithDistance.forEach(({ element }) => container.appendChild(element));

    // Add preferred store indicator
    const preferredStore = storesWithDistance.find(s => s.isPreferred);
    if (preferredStore && !preferredStore.element.querySelector('.preferred-store-indicator')) {
      const indicator = document.createElement('div');
      indicator.className = 'preferred-store-indicator';
      indicator.style.cssText = 'background: #e8f5e8; border-left: 3px solid #00a14b; padding: 4px 8px; margin: -8px -8px 8px -8px; font-size: 11px; color: #00a14b; font-weight: bold;';
      indicator.textContent = 'My Preferred Store';

      const storeInfo = preferredStore.element.querySelector('.store-availability__info');
      if (storeInfo) {
        storeInfo.insertBefore(indicator, storeInfo.firstChild);
      } else {
        preferredStore.element.insertBefore(indicator, preferredStore.element.firstChild);
      }
    }

    this._isUpdating = false;
  }

  updateDrawerStoreButtons() {
    if (!this.drawer || !this.userStoreId) return;

    const allButtons = this.drawer.querySelectorAll('.store-availability-favorite');
    allButtons.forEach(button => {
      button.textContent = 'Set as my store';
      button.classList.remove('store-availability-favorite--enabled');
    });

    const selectedStore = this.drawer.querySelector(`[data-store-id="${this.userStoreId}"]`);
    if (selectedStore) {
      const selectedButton = selectedStore.querySelector('.store-availability-favorite');
      if (selectedButton) {
        selectedButton.textContent = 'My store';
        selectedButton.classList.add('store-availability-favorite--enabled');
      }

      const parentList = selectedStore.parentElement;
      if (parentList && (parentList.tagName === 'UL' || parentList.classList.contains('drawer__scrollable'))) {
        parentList.prepend(selectedStore);
      }
    }
  }

  diagnosePickupAvailability() {
    // Diagnostic function - kept for debugging if needed
    const storeItems = this.drawer ?
      this.drawer.querySelectorAll('[data-store-id]').length :
      'No drawer available';
  }
}

// Add global calculateDistance fallback if not available from store locator
if (typeof window.calculateDistance !== 'function') {
  window.calculateDistance = function(lat1, lon1, lat2, lon2) {
    if (typeof lat1 !== 'number' || typeof lon1 !== 'number' ||
        typeof lat2 !== 'number' || typeof lon2 !== 'number' ||
        isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
      return Infinity;
    }

    const R = 3958.8; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;

    return distance;
  };
}

// Single initialization point on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  // Small delay to ensure DOM is ready
  setTimeout(() => {
    window.pickupAvailability = new PickupAvailability();
  }, 500);
});

// Handle page:loaded for AJAX navigation (won't create duplicate if instance exists)
document.addEventListener('page:loaded', () => {
  setTimeout(() => {
    new PickupAvailability(); // Will return existing instance if already initialized
  }, 500);
});
